import React, { useState, useEffect } from 'react';
import { useAuth, useTrip, healthCheckService } from '@trip-planner-monorepo/shared';
import './HealthCheckModal.css';

const HealthCheckModal = ({ isOpen, onClose, trip }) => {
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [results, setResults] = useState(null);
  const [issues, setIssues] = useState([]);
  const [fixesApplied, setFixesApplied] = useState(false);
  const [scanError, setScanError] = useState(null);
  const { currentUser } = useAuth();
  const { effectiveUserId } = useTrip();

  useEffect(() => {
    if (isOpen) {
      // `trip` is also a dependency: TripContext can still be hydrating when the modal
      // is opened right after navigation (e.g. a direct URL load), in which case the
      // first run below no-ops via the `!trip` guard. Re-running once `trip` arrives
      // starts the scan instead of leaving the modal stuck on its initial, empty state.
      startScan();
    } else {
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, trip?.id]);

  const resetState = () => {
    setScanning(false);
    setProgress(0);
    setCurrentStep('');
    setResults(null);
    setIssues([]);
    setFixesApplied(false);
    setScanError(null);
  };

  const startScan = async () => {
    if (!currentUser || !trip) return;

    setScanning(true);
    setResults(null);
    setIssues([]);
    setScanError(null);
    setProgress(0);

    try {
      const foundIssues = await healthCheckService.runHealthCheck(
        effectiveUserId,
        trip.id,
        (stepName, progressValue) => {
          setCurrentStep(stepName);
          setProgress(progressValue);
        }
      );

      setIssues(foundIssues);
      setResults(foundIssues.length === 0 ? 'no-issues' : 'issues-found');
    } catch (error) {
      console.error('Health check failed:', error);
      setScanError(error.message || 'The scan could not be completed.');
      setResults('scan-error');
    } finally {
      setScanning(false);
    }
  };

  const handleAutoFix = async (issueId) => {
    const issue = issues.find(i => i.id === issueId);
    if (!issue) return;

    try {
      const success = await healthCheckService.fixIssue(effectiveUserId, trip.id, issue);
      if (success) {
        setFixesApplied(true);
        setIssues((prev) => {
          const next = issue.type === 'registry-parity'
            ? prev.filter((item) => item.type !== 'registry-parity')
            : issue.type === 'registry-link-audit'
              ? prev.filter((item) => item.type !== 'registry-link-audit')
              : prev.filter((item) => item.id !== issueId);
          if (next.length === 0) {
            setResults('no-issues');
          }
          return next;
        });
      }
    } catch (error) {
      console.error('Failed to auto-fix issue:', error);
      // Optionally show error toast
    }
  };

  const handleIgnore = (issueId) => {
    const issue = issues.find((item) => item.id === issueId);
    setIssues((prev) => {
      const next = issue?.type === 'registry-parity'
        ? prev.filter((item) => item.type !== 'registry-parity')
        : prev.filter((item) => item.id !== issueId);
      if (next.length === 0) {
        setResults('no-issues');
      }
      return next;
    });
  };

  const handleClose = () => {
    if (fixesApplied) {
      // Reload the page to reflect the changes made by auto-fix
      window.location.reload();
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="health-check-modal-overlay">
      <div className="health-check-modal">
        <div className="health-check-header">
          <h2>🔍 Trip Health Check</h2>
          <button className="close-button" onClick={handleClose}>✕ Close</button>
        </div>

        <div className="health-check-content">
          {scanning ? (
            <div className="scanning-view">
              <div className="scanning-status">
                <p>Scanning trip data for issues...</p>
                <div className="progress-bar-container">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <div className="progress-info">
                  <span>{progress}%</span>
                </div>
              </div>
              <p className="current-step">{currentStep}</p>
            </div>
          ) : results === 'no-issues' ? (
            <div className="results-view success">
              <div className="success-icon">✅</div>
              <h3>Finished Scanning</h3>
              <p>No issues found</p>
              <p className="sub-text">Your trip data is healthy and consistent.</p>
            </div>
          ) : results === 'scan-error' ? (
            <div className="results-view error">
              <div className="error-icon">⚠️</div>
              <h3>Scan Failed</h3>
              <p>{scanError}</p>
              <p className="sub-text">Try again, or contact support if this keeps happening.</p>
              <button className="action-button" onClick={startScan}>Retry Scan</button>
            </div>
          ) : results === 'issues-found' ? (
            <div className="results-view issues">
              <h3>Health Issues for this Trip</h3>
              <div className="issues-list">
                {issues.map(issue => (
                  <div key={issue.id} className="issue-card">
                    <div className="issue-header">
                      <span className="issue-title">{issue.title}</span>
                    </div>
                    <p className="issue-description">{issue.description}</p>
                    <div className="issue-actions">
                      {issue.canAutoFix && (
                        <button
                          className="action-button auto-fix"
                          onClick={() => handleAutoFix(issue.id)}
                          title={
                            issue.type === 'registry-parity'
                              ? 'Sync registry with legacy data: add missing registry links and remove duplicate legacy copies'
                              : issue.type === 'registry-link-audit'
                                ? 'Repair accommodation↔day legacy blocks (registry_only drift) and add missing reverse links'
                                : 'Automatically fix this issue by removing the broken or orphaned reference'
                          }
                        >
                          Auto-Fix
                        </button>
                      )}
                      <button
                        className="action-button ignore"
                        onClick={() => handleIgnore(issue.id)}
                        title="Dismiss this issue from this list without making any changes"
                      >
                        Ignore
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Neither scanning nor a settled result yet — e.g. the modal opened before
            // trip data finished hydrating. Shows a neutral waiting state instead of
            // defaulting into the issues heading with nothing under it.
            <div className="results-view waiting">
              <p>Waiting for trip data...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HealthCheckModal;
